/**
 * <EmployeesAddModal> — invite a new employee.
 *
 * Phase-2 Wave-4 W4.T2. Two-step modal:
 *
 *   Step 1: form (ФИО / email / phone / department / role).
 *           - РО:    department locked to own; role limited to
 *                    EMPLOYEE / LEAD_ENGINEER (per Phase-2 RBAC).
 *           - Admin: any department; any role except SUPERVISOR-tier.
 *           - SUPERVISOR: ADMIN-tier roles ONLY; «Отдел» is NOT rendered
 *                    at all (supervisor-add-modal-dept-field). Every role
 *                    a supervisor can assign is deptless, so the field is
 *                    meaningless in every state. Submit still posts
 *                    `departmentId: null` (form default is null).
 *
 *   Step 2: reveal — fresh inviteUrl with copy + 24h countdown.
 *
 * RBAC matrix (PLAN W4.T3 + Phase 2 §40 + RTF L79 + supervisor-invites-
 * admin 2026-06-10):
 *   - Role list comes from `useRolesQuery({ includeUnassignable: ADMIN })`
 *     (the BE downgrades the flag for non-ADMIN actors). We then filter
 *     client-side via `visibleAssignableRoles` using `ROLE_ACCESS_LEVEL`
 *     from `@aris/shared` so we don't have a hardcoded tier list
 *     (V2-FIX-17 / F-13).
 *   - RTF L79: Admin handles ONLY Admin/РО tiers. Сотрудник invites are
 *     the РО's responsibility — Admin sees neither EMPLOYEE-tier nor
 *     SUPERVISOR-tier in the role select.
 *   - For MANAGER actors we keep only EMPLOYEE-tier roles.
 *   - supervisor-invites-admin: SUPERVISOR sees ADMIN-tier roles ONLY
 *     (tier EQUALITY) — `visibleAssignableRoles` returns the ADMIN-tier
 *     set for SUPERVISOR. Because every role a supervisor can assign is
 *     deptless, the «Отдел» field is hidden outright for this actor
 *     (supervisor-add-modal-dept-field) rather than relying on the
 *     «ADMIN-tier ⇒ departmentId=null + disabled select» path — see the
 *     `hideDepartmentField` invariant below. Submit still posts
 *     departmentId=null (form default).
 *
 * After successful create the modal flips to step 2; the parent's close
 * handler triggers list invalidation (V2-FIX-18 / risk #2 — already
 * handled by `useCreateInviteMutation`'s onSuccess).
 */
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';

import {
  CreateInviteRequestSchema,
  ROLE_ACCESS_LEVEL,
  RoleSlug,
  type AccessLevel,
  type CreateInviteRequest,
} from '@aris/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/fetcher';

import { useDepartmentsQuery } from '@/api/departments';
import { useRolesQuery, type RoleRow } from '@/api/roles';
import { useCreateInviteMutation } from '@/api/invites';
import { useGipUsersQuery } from '@/api/users';

import { TokenLinkDisplay } from './token-link-display';
import {
  ADMIN_TIER_SUPERVISOR_ONLY_HINT,
  visibleAssignableRoles as visibleAssignableRolesShared,
} from './visible-roles';

// ─── Actor + RBAC helpers ────────────────────────────────────────────────

export interface AddActor {
  accessLevel: AccessLevel;
  departmentId: number | null;
  /**
   * Feature 1 «Помощник ГИПа» (Wave 5). The inviter's own id + role slug.
   * Used ONLY to drive the auto-bind UX when inviting an ASSISTANT_GIP: a
   * GIP inviter auto-binds the new assistant to themselves (the BE forces
   * `assistantOfUserId = inviter.id` and ignores the body), so we hide the
   * GIP-picker for that actor. A non-GIP ADMIN/SUPERVISOR inviter must pick
   * a GIP (the picker is required). Both optional so existing call sites /
   * tests that build a minimal actor keep compiling; a missing `roleSlug`
   * is treated as «not a GIP» (picker shown).
   */
  id?: string;
  roleSlug?: RoleSlug;
}

/**
 * Re-export of the shared helper (`./visible-roles`) so existing call
 * sites + the test file keep working. Implementation moved out so the
 * detail modal can use the same RBAC clamp (devil-advocate post-deploy
 * bar 1 — Admin previously saw EMPLOYEE-tier roles in the edit form).
 *
 * Р-114: the shared helper now takes a `surface` because the invite and
 * edit endpoints disagree for a SUPERVISOR actor. This modal is the
 * INVITE surface (`POST /invites` → `assertCanInvite`, tier EQUALITY for a
 * supervisor), so the argument is bound here and NOT exposed to callers —
 * every option this modal renders must be submittable to `/invites`.
 */
export const visibleAssignableRoles = (actor: AddActor, all: RoleRow[]): RoleRow[] =>
  visibleAssignableRolesShared(actor, all, 'invite');

// ─── Form types ─────────────────────────────────────────────────────────

interface FormValues {
  fullName: string;
  email: string;
  departmentId: number | null;
  roleSlug: RoleSlug | '';
  /** R6.4 — joining date (ISO `YYYY-MM-DD`). Defaults to today on create. */
  hireDate: string;
  /**
   * Feature 1 «Помощник ГИПа» (Wave 5). The GIP this assistant binds to.
   * Only meaningful when `roleSlug === 'ASSISTANT_GIP'` AND the inviter is
   * not a GIP (a GIP auto-binds to itself, BE-side). Empty string = unset.
   */
  assistantOfUserId: string | '';
}

const NULL_DEPT = '__none__';

/**
 * R6.4 — today as `YYYY-MM-DD` in the user's local timezone. Used as
 * the default for the «Дата приёма» field. The BE then stores it as a
 * `DATE` (no time component), so timezone drift is irrelevant for the
 * stored value — but matching the user's local notion of «today» here
 * avoids the off-by-one «yesterday-by-default» UX glitch we'd hit if
 * we anchored to UTC (Europe/Moscow is UTC+3, so 22:00 MSK is already
 * the next UTC day).
 */
function todayLocalIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const DEFAULTS: FormValues = {
  fullName: '',
  email: '',
  departmentId: null,
  roleSlug: '',
  hireDate: '',
  assistantOfUserId: '',
};

/**
 * Resolver schema for the form. We:
 *   - relax `roleSlug` (the empty string is a UI convenience for the
 *     unselected state — guarded at submit), and
 *   - relax `assistantOfUserId` similarly: the form carries `''` for the
 *     unselected GIP-picker state, which is NOT a uuid; we normalise `''`
 *     to `undefined` so the underlying `.uuid().nullable().optional()`
 *     contract accepts the «unset» state and a real pick is validated as a
 *     uuid. The required-when-ASSISTANT_GIP rule is enforced at submit
 *     (mirroring the BE `ASSISTANT_BINDING_REQUIRED`), because zod can't
 *     see the actor identity that decides whether the field is required.
 */
const AddInviteResolverSchema = CreateInviteRequestSchema.partial({
  roleSlug: true,
}).extend({
  assistantOfUserId: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().uuid().nullable().optional(),
  ),
});

// ─── Component ──────────────────────────────────────────────────────────

export interface EmployeesAddModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  actor: AddActor;
}

interface RevealState {
  inviteUrl: string;
  expiresAt: string;
}

export function EmployeesAddModal(props: EmployeesAddModalProps): React.ReactElement {
  const { open, onOpenChange, actor } = props;

  const departmentsQuery = useDepartmentsQuery();
  const rolesQuery = useRolesQuery({ includeUnassignable: actor.accessLevel === 'ADMIN' });
  const createMutation = useCreateInviteMutation();

  const [reveal, setReveal] = React.useState<RevealState | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(AddInviteResolverSchema),
    defaultValues: DEFAULTS,
    mode: 'onSubmit',
  });

  // For РО, lock the department to own dept on every fresh mount.
  // R6.4 — also seed `hireDate` to today on every fresh open so the
  // admin doesn't have to type the date by default; they can still
  // pick a past or future date manually.
  React.useEffect(() => {
    if (!open) return;
    if (actor.accessLevel === 'MANAGER' && actor.departmentId !== null) {
      form.setValue('departmentId', actor.departmentId, { shouldDirty: false });
    }
    if (!form.getValues('hireDate')) {
      form.setValue('hireDate', todayLocalIso(), { shouldDirty: false });
    }
  }, [open, actor.accessLevel, actor.departmentId, form]);

  React.useEffect(() => {
    if (!open) {
      setReveal(null);
      form.reset(DEFAULTS);
    }
  }, [open, form]);

  const visibleRoles = React.useMemo(
    () => visibleAssignableRoles(actor, rolesQuery.data ?? []),
    [actor, rolesQuery.data],
  );

  // R6.2: when the selected role's accessLevel is MANAGER, the «one
  // active РО per department» trigger (migration 002) blocks activation
  // of a second РО. Surface the constraint at issue-time by hiding
  // departments that already have an active РО. For other tiers,
  // show the full list as before.
  const selectedRoleSlug = form.watch('roleSlug');
  const selectedRoleAccessLevel: AccessLevel | null = React.useMemo(() => {
    if (!selectedRoleSlug) return null;
    return ROLE_ACCESS_LEVEL[selectedRoleSlug as RoleSlug] ?? null;
  }, [selectedRoleSlug]);

  const visibleDepartments = React.useMemo(() => {
    const all = departmentsQuery.data ?? [];
    if (selectedRoleAccessLevel === 'MANAGER') {
      return all.filter((d) => !d.hasActiveManager);
    }
    return all;
  }, [departmentsQuery.data, selectedRoleAccessLevel]);

  const allDepartmentsBusy =
    selectedRoleAccessLevel === 'MANAGER' &&
    (departmentsQuery.data?.length ?? 0) > 0 &&
    visibleDepartments.length === 0;

  // R6.2: when the role changes to MANAGER and the currently selected
  // department is now busy, clear the dept selection so the user is
  // forced to pick a free one (rather than silently submitting the
  // stale value and getting a 422).
  React.useEffect(() => {
    if (selectedRoleAccessLevel !== 'MANAGER') return;
    const currentDeptId = form.getValues('departmentId');
    if (currentDeptId === null || currentDeptId === undefined) return;
    const stillFree = visibleDepartments.some((d) => d.id === currentDeptId);
    if (!stillFree) {
      form.setValue('departmentId', null, { shouldDirty: true });
    }
  }, [selectedRoleAccessLevel, visibleDepartments, form]);

  // R6.3: ADMIN/SUPERVISOR-tier users MUST have departmentId=NULL
  // (DB CHECK `user_dept_match_role`). Force-clear the dept selection
  // when the chosen role is admin-tier and disable the field so the
  // UI matches the server-side constraint.
  const isAdminTierRole =
    selectedRoleAccessLevel === 'ADMIN' || selectedRoleAccessLevel === 'SUPERVISOR';

  React.useEffect(() => {
    if (!isAdminTierRole) return;
    const currentDeptId = form.getValues('departmentId');
    if (currentDeptId !== null && currentDeptId !== undefined) {
      form.setValue('departmentId', null, { shouldDirty: true });
    }
  }, [isAdminTierRole, form]);

  // Feature 1 «Помощник ГИПа» (Wave 5).
  const isAssistantGipSelected = selectedRoleSlug === RoleSlug.ASSISTANT_GIP;
  // A GIP inviter auto-binds the new assistant to themselves — the BE
  // forces `assistantOfUserId = inviter.id` and ignores the body. So the
  // GIP-picker is hidden for a GIP actor and shown (required) for a non-GIP
  // ADMIN/SUPERVISOR inviter. A missing `roleSlug` on the actor (legacy /
  // test minimal actor) is treated as «not a GIP» → picker shown.
  const actorIsGip = actor.roleSlug === RoleSlug.GIP;
  const showGipPicker = isAssistantGipSelected && !actorIsGip;
  // Only fetch GIPs when the picker is actually shown (avoids a needless
  // round-trip on every modal open).
  const gipUsersQuery = useGipUsersQuery({ enabled: showGipPicker });
  const gipOptions = React.useMemo(() => gipUsersQuery.data?.rows ?? [], [gipUsersQuery.data]);

  // When the role moves away from ASSISTANT_GIP, clear any stale GIP pick so
  // a subsequent re-select / submit never smuggles a binding for a non-
  // assistant role (the BE would 422 ASSISTANT_BINDING_NOT_ALLOWED).
  React.useEffect(() => {
    if (isAssistantGipSelected) return;
    if (form.getValues('assistantOfUserId') !== '') {
      form.setValue('assistantOfUserId', '', { shouldDirty: true });
    }
  }, [isAssistantGipSelected, form]);

  // Department is disabled when:
  //   - the actor is a РО (MANAGER) — dept locked to own; OR
  //   - the chosen role is admin-tier — dept MUST be NULL per DB rule.
  const isDeptDisabled = actor.accessLevel === 'MANAGER' || isAdminTierRole;

  // supervisor-add-modal-dept-field: a SUPERVISOR can ONLY assign
  // ADMIN-tier roles ({GIP, TECH_DIRECTOR, BIM_MANAGER} — all deptless),
  // so «Отдел» is meaningless for this actor in EVERY state: misleading
  // before a role is picked (the dept would be force-nulled on submit)
  // and merely disabled-with-a-hint after. Hide the whole field for the
  // supervisor actor — submit still posts `departmentId: null` because
  // the form default is null and no SUPERVISOR code path mutates it to a
  // non-null value (the MANAGER own-dept lock never fires; the admin-tier
  // effect only re-asserts null). ADMIN / MANAGER actors are unaffected.
  //
  // INVARIANT (load-bearing): hiding the dept field is safe ONLY because
  // `visibleAssignableRoles` (./visible-roles.ts) clamps SUPERVISOR to
  // deptless ADMIN-tier roles. If that clamp is ever widened to include a
  // dept-bearing tier (e.g. MANAGER), this `hideDepartmentField` branch
  // MUST be revisited — otherwise the supervisor would silently post
  // `departmentId: null` for a role that requires a department, yielding a
  // 422 with no UI affordance to fix it. Keep the two in lockstep.
  //
  // Р-114 (2026-09-03) — the clamp WAS widened, but only on the EDIT
  // surface (detail modal). This modal binds `surface: 'invite'` (see the
  // `visibleAssignableRoles` wrapper above), where the supervisor still
  // gets deptless ADMIN-tier roles ONLY — because `assertCanInvite` still
  // enforces tier EQUALITY for him. So the invariant HOLDS unchanged. It
  // is now guarded by a test that pins the invite clamp to the ADMIN four
  // (visible-roles.test.ts): widening THIS surface would turn that test
  // red before it could reach this branch.
  //
  // Feature 1 «Помощник ГИПа» (Wave 5): ASSISTANT_GIP is ADMIN-tier ⇒
  // dept MUST be NULL (DB CHECK `user_dept_match_role`; the BE force-nulls
  // it regardless). The `isAdminTierRole` path above already disables the
  // field and force-clears the value, but we HIDE it outright when this
  // role is selected — the «Отдел» control is meaningless for an assistant
  // and the SUPERVISOR pattern above already establishes hiding (not just
  // disabling) as the idiom for a guaranteed-deptless role. Safe because
  // `selectedRoleAccessLevel === 'ADMIN'` here keeps the force-null effect
  // active, so submit always posts `departmentId: null`.
  const hideDepartmentField = actor.accessLevel === 'SUPERVISOR' || isAssistantGipSelected;

  const onSubmit = React.useCallback(
    async (raw: FormValues) => {
      if (!raw.roleSlug) {
        form.setError('roleSlug', { message: 'Выберите роль' });
        return;
      }
      // Feature 1 «Помощник ГИПа» (Wave 5). Resolve the binding to submit:
      //   - GIP actor inviting an assistant → omit the field; the BE forces
      //     `assistantOfUserId = inviter.id` and ignores any body value.
      //   - non-GIP ADMIN/SUPERVISOR inviting an assistant → REQUIRE a pick
      //     (mirrors BE `ASSISTANT_BINDING_REQUIRED`). Inline the error on
      //     the picker field so the user knows what to fix.
      //   - any other role → never send a binding.
      const invitingAssistant = raw.roleSlug === RoleSlug.ASSISTANT_GIP;
      let assistantBinding: string | undefined;
      if (invitingAssistant && !actorIsGip) {
        if (!raw.assistantOfUserId) {
          form.setError('assistantOfUserId', {
            message: 'Укажите ГИПа, которому назначается помощник',
          });
          return;
        }
        assistantBinding = raw.assistantOfUserId;
      }
      const body: CreateInviteRequest = {
        fullName: raw.fullName,
        email: raw.email,
        departmentId: raw.departmentId,
        roleSlug: raw.roleSlug,
        // R6.4 — surface hireDate only when set. The BE falls back to
        // today via the column DEFAULT (CURRENT_DATE per migration 025)
        // if we omit it, but in practice we always seed today on open.
        ...(raw.hireDate ? { hireDate: raw.hireDate } : {}),
        // Only include the binding for the non-GIP-actor assistant case.
        ...(assistantBinding !== undefined ? { assistantOfUserId: assistantBinding } : {}),
      };
      try {
        const result = await createMutation.mutateAsync(body);
        setReveal({ inviteUrl: result.inviteUrl, expiresAt: result.expiresAt });
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 409 && err.code === 'EMAIL_TAKEN') {
            form.setError('email', { message: 'Этот email уже используется' });
            return;
          }
          // R6.2: inline the «one active РО per dept» conflict under
          // the dept field. The BE returns the conflicting РО's name
          // in the message so the user knows who to archive.
          if (err.status === 422 && err.code === 'DEPT_HAS_ACTIVE_RO') {
            form.setError('departmentId', { message: err.message });
            return;
          }
          // «Отдел обязателен для этой роли». Схема не может знать правило
          // (departmentId в контракте optional — для admin-tier ролей отдела
          // нет), поэтому его проверяет сервер. Показываем под полем «Отдел»,
          // а не тостом: тост уводит взгляд от поля, которое надо заполнить.
          if (err.status === 422 && err.code === 'DEPARTMENT_REQUIRED') {
            form.setError('departmentId', { message: err.message }, { shouldFocus: true });
            return;
          }
          // Feature 1 «Помощник ГИПа» (Wave 5): surface the BE's clean
          // binding errors inline on the GIP-picker field. The FE guards
          // the required-pick path above, so these are the defense-in-depth
          // surfaces (e.g. the picked GIP was archived between fetch and
          // submit → TARGET_INVALID).
          if (
            err.status === 422 &&
            (err.code === 'ASSISTANT_BINDING_REQUIRED' ||
              err.code === 'ASSISTANT_BINDING_TARGET_INVALID' ||
              err.code === 'ASSISTANT_BINDING_NOT_ALLOWED')
          ) {
            form.setError('assistantOfUserId', { message: err.message });
            return;
          }
          if (err.status === 422 && err.code === 'VALIDATION_ERROR') {
            // R6.4 — generic 422 from the hireDate parser (or any other
            // schema rejection). Surface inline on hireDate if the
            // message mentions the field; otherwise fall through to
            // the toast path below.
            if (/дат[аы]/i.test(err.message)) {
              form.setError('hireDate', { message: err.message });
              return;
            }
          }
          if (err.status === 422) {
            toast(err.message, { variant: 'error' });
            return;
          }
          toast(err.message, { variant: 'error' });
          return;
        }
        toast('Не удалось создать приглашение.', { variant: 'error' });
      }
    },
    [createMutation, form, actorIsGip],
  );

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="employees-add-modal">
        <DialogHeader>
          <DialogTitle>Добавить сотрудника</DialogTitle>
          <DialogDescription>
            {reveal === null
              ? 'Заполните данные и отправьте приглашение.'
              : 'Скопируйте ссылку — она будет действительна 24 часа.'}
          </DialogDescription>
        </DialogHeader>

        {departmentsQuery.isLoading || rolesQuery.isLoading ? (
          <Skeleton className="h-40 w-full" data-testid="employees-add-loading" />
        ) : reveal === null ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-3"
              data-testid="employees-add-form"
            >
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ФИО</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="off" data-testid="employees-add-fullName" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="off"
                        data-testid="employees-add-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* R6.4 — Дата приёма на работу. Defaults to today;
                  admin may pick any past or future date. The BE
                  rejects workload plan/fact writes for dates before
                  this value (per-employee, server-side). */}
              <FormField
                control={form.control}
                name="hireDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Дата приёма на работу</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="date"
                        autoComplete="off"
                        data-testid="employees-add-hireDate"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="roleSlug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Роль</FormLabel>
                    <Select
                      value={field.value === '' ? undefined : field.value}
                      onValueChange={(v) => field.onChange(v as RoleSlug)}
                    >
                      <SelectTrigger aria-label="Роль" data-testid="employees-add-role">
                        <SelectValue placeholder="Выберите роль" />
                      </SelectTrigger>
                      <SelectContent>
                        {visibleRoles.map((r) => (
                          <SelectItem key={r.id} value={r.slug}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* GLOBAL rule: a non-supervisor actor no longer sees any
                        ADMIN-tier option in the select above (clamped to
                        MANAGER-tier by `visibleAssignableRoles`). Explain why,
                        pointing the user at the supervisor. Hidden for a
                        SUPERVISOR actor — they CAN assign admin-tier. */}
                    {actor.accessLevel !== 'SUPERVISOR' ? (
                      <p
                        className="text-xs text-muted"
                        data-testid="employees-add-role-supervisor-hint"
                      >
                        {ADMIN_TIER_SUPERVISOR_ONLY_HINT}
                      </p>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Feature 1 «Помощник ГИПа» (Wave 5). The GIP-picker shows
                  ONLY when ASSISTANT_GIP is selected AND the inviter is not
                  a GIP (a GIP auto-binds the assistant to itself, BE-side).
                  Required for the non-GIP ADMIN/SUPERVISOR inviter. */}
              {showGipPicker ? (
                <FormField
                  control={form.control}
                  name="assistantOfUserId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ГИП</FormLabel>
                      <Select
                        value={field.value === '' ? undefined : field.value}
                        onValueChange={(v) => field.onChange(v)}
                        disabled={gipUsersQuery.isLoading}
                      >
                        <SelectTrigger aria-label="ГИП" data-testid="employees-add-gip">
                          <SelectValue placeholder="Выберите ГИПа" />
                        </SelectTrigger>
                        <SelectContent>
                          {gipOptions.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.fullName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {gipOptions.length === 0 && !gipUsersQuery.isLoading ? (
                        <p
                          className="text-xs text-muted"
                          data-testid="employees-add-gip-empty-hint"
                          aria-live="polite"
                        >
                          Нет активных ГИПов. Сначала назначьте ГИПа.
                        </p>
                      ) : null}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
              {hideDepartmentField ? null : (
                <FormField
                  control={form.control}
                  name="departmentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Отдел</FormLabel>
                      <Select
                        value={
                          field.value === null || field.value === undefined
                            ? NULL_DEPT
                            : String(field.value)
                        }
                        onValueChange={(v) => field.onChange(v === NULL_DEPT ? null : Number(v))}
                        disabled={isDeptDisabled}
                      >
                        <SelectTrigger aria-label="Отдел" data-testid="employees-add-department">
                          <SelectValue placeholder="Выберите отдел" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NULL_DEPT}>— без отдела —</SelectItem>
                          {visibleDepartments.map((d) => (
                            <SelectItem key={d.id} value={String(d.id)}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isAdminTierRole ? (
                        <p
                          className="text-xs text-muted"
                          data-testid="employees-add-department-admin-hint"
                        >
                          Для этой роли отдел не назначается.
                        </p>
                      ) : allDepartmentsBusy ? (
                        <p
                          className="text-xs text-muted"
                          data-testid="employees-add-department-empty-hint"
                        >
                          Все отделы уже имеют активных руководителей. Архивируйте кого-нибудь,
                          чтобы освободить отдел.
                        </p>
                      ) : null}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  data-testid="employees-add-cancel"
                >
                  <X className="mr-1 size-3.5" aria-hidden="true" />
                  Отмена
                </Button>
                <Button
                  type="submit"
                  data-testid="employees-add-submit"
                  disabled={createMutation.isPending}
                >
                  <Plus className="mr-1 size-3.5" aria-hidden="true" />
                  Создать приглашение
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : (
          <div className="space-y-4" data-testid="employees-add-reveal">
            <p className="text-sm text-ink">
              Приглашение создано. Передайте ссылку сотруднику любым удобным способом.
            </p>
            <TokenLinkDisplay
              url={reveal.inviteUrl}
              expiresAt={reveal.expiresAt}
              label="Ссылка-приглашение"
              testIdPrefix="employees-add-link"
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  // Allow inviting another employee in one go.
                  setReveal(null);
                  form.reset(DEFAULTS);
                  if (actor.accessLevel === 'MANAGER' && actor.departmentId !== null) {
                    form.setValue('departmentId', actor.departmentId, { shouldDirty: false });
                  }
                }}
                data-testid="employees-add-another"
              >
                Пригласить ещё
              </Button>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="employees-add-close"
              >
                Закрыть
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
